import { FC, useState, useEffect, useMemo, useContext } from 'react'
import { useDisclosure } from '@chakra-ui/react'

import calculateDatesDifferenceInDays from '../../localUtils/calculateDatesDifferenceInDays'
import CalendarFiltersContext, { DateInterval } from './Context'
import { CalendarFiltersButtonProps } from '../..'
import { formatDateHourForAPI } from '../../localUtils/formatDateHourForAPI'

type Props = CalendarFiltersButtonProps & {
  children: JSX.Element
}

const CalendarFiltersDrawerProvider: FC<Props> = ({
  defaultDates,
  onCloseModal,
  onOpenModal,
  onSubmit,
  modalTitle = '',
  children,
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure()

  const [dateFilterType, setDateFilterType] = useState<string>(defaultDates?.dateFilterType || '')
  const [selectedDateInterval, setSelectedDateInterval] = useState<DateInterval>({})
  const [startDate, setStartDate] = useState<string>(defaultDates?.startDate || '')
  const [finalDate, setFinalDate] = useState<string>(defaultDates?.finalDate || '')
  const [startHour, setStartHour] = useState<string>(defaultDates?.startHour || '00:00')
  const [finalHour, setFinalHour] = useState<string>(defaultDates?.finalHour || '00:00')

  function getFeedbackDateStringInRangerCalendars() {
    if (selectedDateInterval?.startDate) return ` ${selectedDateInterval.label}`
    if (!startDate || '') return ''
    if (startDate === finalDate) return `${startDate} dás ${startHour} até ${finalHour}`
    if (startDate || finalDate) return ` ${startDate} ${startHour} até ${finalDate} ${finalHour}`
    return '...'
  }

  function getFeedbackDateStringInSingleCalendar() {
    if (selectedDateInterval?.startDate) return ` ${selectedDateInterval.label}`
    if (!startHour) return ` ${startDate}`
    if (startDate) return ` ${startDate} ás ${startHour}`
    return '...'
  }

  function checkFinalHourIsBiggerThanStartHour() {
    const days = calculateDatesDifferenceInDays(startDate, finalDate)
    if (!finalHour || !startHour) return
    const finalHourSplited = finalHour.split(':')
    const finalHourNumber = Number(`${finalHourSplited[0]}${finalHourSplited[1]}`)
    const startHourSplited = startHour.split(':')
    const startHourNumber = Number(`${startHourSplited[0]}${startHourSplited[1]}`)

    if (dateFilterType === 'Intervalo de datas') {
      if (days === 0 && startHourNumber > finalHourNumber) {
        setStartHour(finalHour)
        setFinalHour(startHour)
      }
    }
  }

  const feedbackDateString = useMemo(() => {
    if (dateFilterType === 'Intervalo de datas') return getFeedbackDateStringInRangerCalendars()
    return getFeedbackDateStringInSingleCalendar()
  }, [dateFilterType, getFeedbackDateStringInSingleCalendar, getFeedbackDateStringInRangerCalendars])

  const closeModal = () => {
    if (onCloseModal) onCloseModal()
    onClose()
  }

  const openModal = () => {
    if (onOpenModal) onOpenModal()
    onOpen()
  }

  const onSubmitCalendar = () => {
    const formatedDatesForAPI = formatDateHourForAPI({ dateFilterType, startDate, finalDate, startHour, finalHour })

    const submitedDates = {
      shortcutLabel: selectedDateInterval.label,
      dateFilterType,
      startDate,
      finalDate,
      startHour,
      finalHour,
      startDataForAPI: formatedDatesForAPI.startDate,
      finalDataForAPI: formatedDatesForAPI.finalDate,
      dateFeedback: feedbackDateString,
    }

    onSubmit(submitedDates)
    closeModal()
  }

  useEffect(() => {
    checkFinalHourIsBiggerThanStartHour()
  }, [finalHour, startHour])

  return (
    <CalendarFiltersContext.Provider
      value={{
        feedbackDateString,
        dateFilterType,
        setDateFilterType,
        isOpen,
        onClose: closeModal,
        onOpen: openModal,
        selectedDateInterval,
        setSelectedDateInterval,
        startDate,
        setStartDate,
        finalDate,
        setFinalDate,
        startHour,
        setStartHour,
        finalHour,
        setFinalHour,
        onSubmitCalendar,
        modalTitle,
      }}
    >
      {children}
    </CalendarFiltersContext.Provider>
  )
}

export const useCalendarFilters = () => useContext(CalendarFiltersContext)

export default CalendarFiltersDrawerProvider
